import { unifiedApi } from '@/lib/api-client';
import { readSuccessorSearch, type MissionOwnershipHandover, type SuccessorSearch } from './memberOwnershipHandover';
import type { InvitableRole, OrganisationRole } from './generated/organisationRoles';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  website?: string;
  contactEmail: string;
  plan: string;
  maxMembers: number;
  maxProjects: number;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  domain?: string;  // Organization domain
  /** Members that are active and not pending (server member_count). */
  memberCount?: number;
  /**
   * Of memberCount, those whose last activity is within the last 30 days
   * (server members_active_last_30_days, agion/Agion#2628). Absent from an
   * older server.
   */
  membersActiveLast30Days?: number;
}

export interface CreateOrganizationResponse extends Organization {
  access_token?: string; // JWT token refreshed with organization context
}

export type AccessAudience = 'internal' | 'customer';

export interface OrganizationMember {
  accessAudience?: AccessAudience;
  id: string;
  organizationId: string;
  userId: string;
  role: OrganisationRole;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  avatar?: string;
  joinedAt: string;
  lastActiveAt?: string | null;
  signInMethods?: string[];
  isActive: boolean;
  isPending: boolean;
  missionsCount?: number;
  agentsCount?: number;
}

export interface DisplayNameResetResult {
  scope: 'global';
  mode: 'directory' | 'clear';
  firstName: string;
  lastName: string;
  provider?: string;
  observedAt?: string;
  wouldChange: boolean;
  changed: boolean;
}

export interface MemberDirectoryOptions {
  includeInactive?: boolean;
}

export interface OrganizationAuditEvent {
  id: string;
  organizationId: string;
  eventType: string;
  actorId: string;
  // The person's name as the server read it from the user directory, also for
  // someone outside this organization (Agion staff, former members). Absent
  // when no person is behind the id (agion/Agion#2256).
  actorName?: string;
  // Whose membership a membership event changed (resource type
  // organization_member), by the name the server read for that person.
  // Absent for other targets and when no person is behind the id (persona
  // run 3 F-AUD-01).
  targetName?: string;
  targetDeleted?: boolean;
  resourceType: string | null;
  resourceId: string | null;
  description: string | null;
  metadata: unknown;
  createdAt: string;
}

export interface OrganizationAuditPage {
  events: OrganizationAuditEvent[];
  nextCursor: string | null;
}

export interface OrganizationAuditOptions {
  cursor?: string;
  eventType?: string;
}

export interface OrganizationInvitation {
  accessAudience?: AccessAudience;
  id: string;
  organizationId: string;
  email: string;
  role: InvitableRole;
  invitedBy: string;
  inviterName?: string;
  message?: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string;
  orgName?: string;
  orgLogo?: string;
}

export interface PendingExternalUser {
  id: string;
  organizationId: string;
  email: string;
  firstName: string;        // Individual first name
  lastName: string;         // Individual last name
  fullName: string;         // Complete name (synthesized from first + last)
  displayName?: string;     // Display name alternative
  domain: string;
  provider: string;
  providerSubject: string;
  userIdentifier?: string;
  role: string;             // Pending role assignment
  status: 'pending' | 'approved' | 'rejected';
  message?: string;
  createdAt: string;
  updatedAt: string;
  approvedAt?: string;
  approvedBy?: string;
}

export interface InviteMemberRequest {
  accessAudience?: AccessAudience;
  email: string;
  role: InvitableRole;
  message?: string;
  invitedBy?: string;  // User ID of the person sending the invitation (optional, handled server-side)
}

// P-009: Allowed domains for multi-org invitations
export interface AllowedDomain {
  id: string;
  domain: string;
  addedBy: string;      // UUID for audit trail
  addedAt: string;
  reason?: string;
  addedByName: string;  // Display name (joined from users table)
}

export interface CreateOrganizationRequest {
  name: string;
  slug?: string;
  description?: string;
  website?: string;
  contactEmail: string;
  contactPhone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  plan?: string;
  requireMFA?: boolean;
}

class OrganizationService {
  async getOrganization(orgId: string): Promise<Organization> {
    const org = await unifiedApi.get<Organization>(`organizations/${orgId}`);
    // The shared converter leaves an underscore before digits, so
    // members_active_last_30_days arrives as membersActiveLast_30Days.
    const record = { ...(org as unknown as Record<string, unknown>) };
    const active = record['membersActiveLast_30Days'] ?? record['members_active_last_30_days'];
    delete record['membersActiveLast_30Days'];
    delete record['members_active_last_30_days'];
    const count = typeof active === 'number' && Number.isInteger(active) && active >= 0 ? active : undefined;
    return { ...(record as unknown as Organization), ...(count !== undefined ? { membersActiveLast30Days: count } : {}) };
  }

  async getMembers(orgId: string, options?: MemberDirectoryOptions): Promise<OrganizationMember[]> {
    const query = options?.includeInactive ? '?include_inactive=true' : '';
    return unifiedApi.get<OrganizationMember[]>(`organizations/${orgId}/members${query}`);
  }

  async getAuditEvents(orgId: string, options: OrganizationAuditOptions = {}): Promise<OrganizationAuditPage> {
    return unifiedApi.getOrganizationAuditEvents(orgId, options);
  }

  async getInvitations(orgId: string): Promise<OrganizationInvitation[]> {
    return unifiedApi.get<OrganizationInvitation[]>(`organizations/${orgId}/invitations`);
  }

  async inviteMember(orgId: string, request: InviteMemberRequest): Promise<void> {
    await unifiedApi.post<void>(`organizations/${orgId}/members/invite`, request);
  }

  async cancelInvitation(orgId: string, invitationId: string): Promise<void> {
    await unifiedApi.delete(`organizations/${orgId}/invitations/${invitationId}`);
  }

  async createOrganization(request: CreateOrganizationRequest): Promise<CreateOrganizationResponse> {
    // API client now handles camelCase to snake_case conversion automatically
    const payload = {
      name: request.name,
      slug: request.slug,
      description: request.description,
      website: request.website,
      contactEmail: request.contactEmail,
      contactPhone: request.contactPhone,
      address: request.address,
      city: request.city,
      state: request.state,
      country: request.country,
      postalCode: request.postalCode,
      plan: request.plan ?? 'free',
      requireMfa: request.requireMFA ?? false,
    };

    return unifiedApi.post<CreateOrganizationResponse>('organizations', payload);
  }

  async resendInvitation(orgId: string, invitationId: string): Promise<void> {
    await unifiedApi.post<void>(`organizations/${orgId}/invitations/${invitationId}/resend`, {});
  }

  /**
   * The only transition between Guest and an internal role (ADR-20260930):
   * customer makes the member a Guest, internal makes a Guest a User. The
   * member's sessions in the organisation end when it changes. A member who
   * owns missions becomes a Guest only with ownershipHandover naming a
   * successor for each (agion/Agion#3043); without it the server answers 409
   * with the owned missions (readOwnershipHandoverRefusal).
   */
  async updateMemberAccessAudience(orgId: string, userId: string, accessAudience: AccessAudience, ownershipHandover?: MissionOwnershipHandover[]): Promise<{ userId: string; accessAudience: AccessAudience; role?: string; changed: boolean; sessionsInvalidated?: boolean; ownershipHandedOver?: MissionOwnershipHandover[] }> {
    return unifiedApi.patch(`organizations/${orgId}/members/${userId}/access-audience`, ownershipHandover ? { accessAudience, ownershipHandover } : { accessAudience });
  }

  /**
   * People who may take over one mission of a member being made a Guest,
   * found by name beyond the 50 the refusal lists (agion/Agion#3043). The
   * server applies the same eligibility as the handover itself.
   */
  async searchHandoverSuccessors(orgId: string, userId: string, missionId: string, query: string): Promise<SuccessorSearch> {
    const answer = await unifiedApi.get<unknown>(`organizations/${orgId}/members/${userId}/handover-successors`, { mission_id: missionId, q: query, limit: 20 });
    return readSuccessorSearch(answer);
  }

  async updateMemberRole(orgId: string, memberId: string, role: string): Promise<void> {
    await unifiedApi.patch<void>(`organizations/${orgId}/members/${memberId}/role`, { role });
  }

  /**
   * An owner changes another owner's role (PLAN-20260912 slice 50). The
   * ordinary role route keeps refusing owner targets (cannot_modify_owner).
   */
  async updateOwnerRole(orgId: string, memberId: string, role: string): Promise<void> {
    await unifiedApi.patch<void>(`organizations/${orgId}/members/${memberId}/owner-role`, { role });
  }

  async previewMemberDisplayNameReset(orgId: string, userId: string): Promise<DisplayNameResetResult> {
    return unifiedApi.get<DisplayNameResetResult>(`organizations/${orgId}/members/${userId}/reset-display-name`);
  }

  async resetMemberDisplayName(orgId: string, userId: string): Promise<DisplayNameResetResult> {
    return unifiedApi.post<DisplayNameResetResult>(`organizations/${orgId}/members/${userId}/reset-display-name`, {});
  }

  async removeMember(orgId: string, memberId: string): Promise<void> {
    await unifiedApi.delete(`organizations/${orgId}/members/${memberId}`);
  }

  /**
   * The signed-in member leaves the organization. When it was their last
   * membership the server also deletes the account (accountDeleted). Owners
   * are refused with code owner_cannot_leave.
   */
  async leaveOrganization(orgId: string): Promise<{ accountDeleted: boolean }> {
    return unifiedApi.delete<{ accountDeleted: boolean }>(`organizations/${orgId}/members/me`);
  }

  async acceptInvitation(token: string): Promise<{ organizationId: string; role: string }> {
    return unifiedApi.post<{ organizationId: string; role: string }>(`invitations/accept`, { token });
  }

  async getPendingExternalUsers(orgId: string): Promise<PendingExternalUser[]> {
    const response = await unifiedApi.get<{ requests: PendingExternalUser[] }>(`organizations/${orgId}/pending-users`);
    return response.requests ?? [];
  }

  async getPendingRegistrations(orgId: string): Promise<PendingExternalUser[]> {
    const response = await unifiedApi.get<{ requests: PendingExternalUser[] }>(`organizations/${orgId}/pending-users`);
    return response.requests ?? [];
  }

  async approvePendingRegistration(pendingId: string, role?: string): Promise<void> {
    const payload = role ? { role } : {};
    await unifiedApi.post<void>(`organizations/pending-users/${pendingId}/approve`, payload);
  }

  async rejectPendingRegistration(pendingId: string, reason?: string): Promise<void> {
    const payload = reason ? { reason } : {};
    await unifiedApi.post<void>(`organizations/pending-users/${pendingId}/reject`, payload);
  }

  async approvePendingExternalUser(requestId: string, role?: string, accessAudience?: AccessAudience): Promise<PendingExternalUser> {
    const payload = { ...(role ? { role } : {}), ...(accessAudience ? { accessAudience } : {}) };
    return unifiedApi.post<PendingExternalUser>(`organizations/pending-users/${requestId}/approve`, payload);
  }

  async rejectPendingExternalUser(requestId: string, message?: string): Promise<PendingExternalUser> {
    return unifiedApi.post<PendingExternalUser>(`organizations/pending-users/${requestId}/reject`, { message });
  }

  async updateOrganization(orgId: string, updates: Partial<Organization>): Promise<Organization> {
    // Backend expects PUT, not PATCH - gateway/internal/handlers/routes.go:30
    return unifiedApi.put<Organization>(`organizations/${orgId}`, updates);
  }

  async deleteOrganization(orgId: string): Promise<void> {
    await unifiedApi.delete(`organizations/${orgId}`);
  }

  // P-009: Allowed Domains Management
  async getAllowedDomains(orgId: string): Promise<AllowedDomain[]> {
    const response = await unifiedApi.get<{ domains: AllowedDomain[] }>(`organizations/${orgId}/allowed-domains`);
    return response.domains ?? [];
  }

  async addAllowedDomain(orgId: string, domain: string, reason?: string): Promise<AllowedDomain> {
    return unifiedApi.post<AllowedDomain>(`organizations/${orgId}/allowed-domains`, { domain, reason });
  }

  async removeAllowedDomain(orgId: string, domainId: string): Promise<void> {
    await unifiedApi.delete(`organizations/${orgId}/allowed-domains/${domainId}`);
  }

  // P-009: Organization Switching
  async getUserOrganizations(): Promise<Organization[]> {
    const response = await unifiedApi.get<{ organizations: Organization[] }>('users/me/organizations');
    return response.organizations ?? [];
  }

  async switchOrganization(orgId: string): Promise<void> {
    await unifiedApi.post<void>('users/me/switch-organization', { organizationId: orgId });
  }

  // P-009: Invitation Validation (for new user registration)
  async validateInvitation(token: string): Promise<{
    valid: boolean;
    email?: string;
    organizationName?: string;
    role?: string;
    inviterName?: string;
    expiresAt?: string;
    requiresVerificationCode: boolean;
  }> {
    return unifiedApi.get<{
      valid: boolean;
      email?: string;
      organizationName?: string;
      role?: string;
      inviterName?: string;
      expiresAt?: string;
      requiresVerificationCode: boolean;
    }>(`invitations/validate?token=${encodeURIComponent(token)}`);
  }
}

export const organizationService = new OrganizationService();